import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

const languages = [
  { code: "hu", name: "Magyar", country_code: "hu" },
  { code: "en", name: "English", country_code: "gb" },
  { code: "de", name: "Deutsch", country_code: "de" },
  { code: "es", name: "Español", country_code: "es" },
  { code: "fr", name: "Français", country_code: "fr" },
  { code: "it", name: "Italiano", country_code: "it" },
  { code: "cn", name: "中國人", country_code: "cn" },
  { code: "cz", name: "Čech", country_code: "cz" },
  { code: "ru", name: "Русский", country_code: "ru" },
];

const GlobeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={24}
    height={24}
    fill="currentColor"
    className="bi bi-globe"
    viewBox="0 0 16 16"
  >
    <path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m7.5-6.923c-.67.204-1.335.82-1.887 1.855A8 8 0 0 0 5.145 4H7.5zM4.09 4a9.3 9.3 0 0 1 .64-1.539 7 7 0 0 1 .597-.933A7.03 7.03 0 0 0 2.255 4zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a7 7 0 0 0-.656 2.5zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5zM8.5 5v2.5h2.99a12.5 12.5 0 0 0-.337-2.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5zM5.145 12q.208.58.468 1.068c.552 1.035 1.218 1.65 1.887 1.855V12zm.182 2.472a7 7 0 0 1-.597-.933A9.3 9.3 0 0 1 4.09 12H2.255a7 7 0 0 0 3.072 2.472M3.82 11a13.7 13.7 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5zm6.853 3.472A7 7 0 0 0 13.745 12H11.91a9.3 9.3 0 0 1-.64 1.539 7 7 0 0 1-.597.933M8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855q.26-.487.468-1.068zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.7 13.7 13.7 0 0 1-.312 2.5m2.802-3.5a7 7 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7 7 0 0 0-3.072-2.472c.218.284.418.598.597.933M10.855 4a8 8 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4z" />
  </svg>
);


const TranslatedHeader = ( title,
  description,
  keywords,
  url,
  image,
  lang = "en",
  author = "Zsolt Fenyő",) => {
  const { t, i18n } = useTranslation();
  const [navbarOpen, setNavbarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [sticky, setSticky] = useState({ isSticky: false, offset: 0 });
  const headerRef = useRef(null);
  const currentLang = i18n.language;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef();

  const defaultKeywords = [
    "AI",
    "Artificial Intelligence",
    "Machine Learning",
    "Deep Learning",
    "AI tools",
    "GPT-4",
    "AI assistants",
    "AI applications",
    "Neural Networks",
  ];

  const handleLanguageChange = (code) => {
    const currentPath = window.location.pathname;
    const regex = /^\/[a-z]{2}/;
    const newPath = currentPath.replace(regex, `/${code}`);
    i18n.changeLanguage(code).then(() => {
      navigate(newPath);
    });
    setDropdownOpen(false);
  };

  const handleScroll = () => {
    const header = headerRef.current;
    if (!header) return;
    const headerRect = header.getBoundingClientRect();
    const isNowSticky = window.scrollY > headerRect.top + headerRect.height;
    if (sticky.isSticky !== isNowSticky) {
      setSticky({
        isSticky: isNowSticky,
        offset: isNowSticky ? headerRect.height : 0,
      });
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const createLocalizedLink = (path) => `/${currentLang}${path}`;
  const localizedUrl = `https://aiinsidelife.com${createLocalizedLink(
    "/Home"
  )}`;
  const handleNavLinkClick = () => setNavbarOpen(false);

  return (
    <header>
      <Helmet htmlAttributes={{ lang }}>
      {/* Title & Description */}
      <title>AiinsideLife Article</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={[...defaultKeywords, ...(keywords || [])].join(", ")} />
      <meta name="author" content={author} />

      {/* Dublin Core */}
      <meta name="DC.title" content={title} />
      <meta name="DC.creator" content={author} />
      <meta name="DC.subject" content={[...defaultKeywords, ...(keywords || [])].join(", ")} />
      <meta name="DC.description" content={description} />
      <meta name="DC.publisher" content={author} />
      <meta name="DC.date" content={new Date().toISOString().split("T")[0]} />
      <meta name="DC.language" content={lang} />
      <meta name="DC.identifier" content={url} />
      <meta name="DC.rights" content={`© ${new Date().getFullYear()} ${author}. All rights reserved.`} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image || "/default-og-image.png"} />
      <meta property="og:site_name" content={author} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image || "/default-og-image.png"} />

      {/* Canonical */}
      <link rel="canonical" href={url} />

      {/* JSON-LD Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: title,
          url: url,
          description: description,
          publisher: {
            "@type": "Organization",
            name: author,
            logo: { "@type": "ImageObject", url: image || "/default-og-image.png" },
          },
        })}
      </script>
    </Helmet>
        
      <h1 className="visually-hidden">{t("Page_Title")}</h1>

      <nav
        className={`navbar navbar-expand-lg bg-body-tertiary ${
          sticky.isSticky ? "sticky" : ""
        }`}
        ref={headerRef}
        id="sticky-header"
      >
        <div className="container-fluid">
          <NavLink className="navbar-brand" to={createLocalizedLink("/Home")}>
            <img src="/images/gptLogo.webp" alt="Logo" className="gtp" />
            {t("Brand_name")}
          </NavLink>
          <button
            className="navbar-toggler"
            type="button"
            onClick={() => setNavbarOpen(!navbarOpen)}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div
            className={`collapse navbar-collapse ${navbarOpen ? "show" : ""}`}
          >
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              {["/Home", "/Blog", "/Aitools", "/Learning", "/Contact"].map(
                (path, i) => (
                  <li className="nav-item" key={path}>
                    <NavLink
                      className="nav-link"
                      to={createLocalizedLink(path)}
                      onClick={handleNavLinkClick}
                    >
                      {t(`Site_${i + 1}`)}
                    </NavLink>
                  </li>
                )
              )}
            </ul>

            {/* SAJÁT DROPDOWN – nem Bootstrap */}
            <div className="position-relative" ref={dropdownRef}>
              <button
                className="btn btn-link"
                onClick={() => setDropdownOpen((prev) => !prev)}
                aria-label={t("Select_language")}
              >
                <GlobeIcon />
              </button>
              {dropdownOpen && (
                <ul
                  className="dropdown-menu show"
                  style={{ position: "absolute", right: 0 }}
                >
                  {languages.map(({ code, name, country_code }) => (
                    <li key={code}>
                      <button
                        className="dropdown-item"
                        onClick={() => handleLanguageChange(code)}
                      >
                        <span
                          className={`flag-icon flag-icon-${country_code} me-2`}
                        />
                        {name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default TranslatedHeader;
